import React, { useEffect, useMemo, useState } from "react";
import { ALL_TOWNS, TOWN_SEEC_ORGANIZATIONS } from "../../../shared/constants/townNames";
import { SEEC_CANDIDATE_COMMITTEE_NAMES, SEEC_CANDIDATE_NAME_TO_CAMPAIGNS } from "../../../shared/constants";

const VIEWS = [
  { id: "town", label: "Town", selected: "selectedSeecTown", setter: "setSelectedSeecTown" },
  { id: "candidate", label: "Candidate committee", selected: "selectedSeecCandidate", setter: "setSelectedSeecCandidate" },
  { id: "politician", label: "Politician", selected: "selectedSeecPolitician", setter: "setSelectedSeecPolitician" },
  { id: "political", label: "Political committee", selected: "selectedSeecPoliticalCommittee", setter: "setSelectedSeecPoliticalCommittee" },
];

export function SeecExplorerControls({ context }) {
  const activeView = VIEWS.find((view) => context[view.selected]);
  const activeId = activeView?.id;
  const activeName = activeView ? context[activeView.selected] : "";
  const [viewId, setViewId] = useState(activeView?.id || "town");
  const [search, setSearch] = useState("");
  useEffect(() => {
    if (activeId) {
      setViewId(activeId);
      setSearch("");
    }
  }, [activeId, activeName]);
  const view = VIEWS.find((entry) => entry.id === viewId);
  const options = useMemo(() => {
    const items = viewId === "town" ? ALL_TOWNS
      : viewId === "candidate" ? SEEC_CANDIDATE_COMMITTEE_NAMES
        : viewId === "politician" ? Object.keys(SEEC_CANDIDATE_NAME_TO_CAMPAIGNS)
          : Object.values(TOWN_SEEC_ORGANIZATIONS).flat();
    return [...new Set(items)].sort((a, b) => a.localeCompare(b));
  }, [viewId]);
  const matches = useMemo(() => {
    const terms = search.toLowerCase().trim().split(/\s+/).filter(Boolean);
    return terms.length ? options.filter((name) => terms.every((term) => name.toLowerCase().includes(term))) : [];
  }, [options, search]);
  const clearSelection = () => VIEWS.forEach((entry) => context[entry.setter]?.(""));
  const select = (name) => {
    VIEWS.forEach((entry) => context[entry.setter]?.(entry.id === viewId ? name : ""));
    setSearch("");
  };

  return (
    <section aria-label="Find campaign finance filings" className="rounded-lg border border-gray-300 bg-white p-4 shadow-sm">
      <div className="flex flex-wrap gap-2" role="group" aria-label="Browse filings by">
        {VIEWS.map((entry) => (
          <button key={entry.id} type="button" aria-pressed={viewId === entry.id}
            onClick={() => { setViewId(entry.id); setSearch(""); clearSelection(); }}
            className={`rounded-md border px-3 py-2 text-sm font-medium ${viewId === entry.id
              ? "border-blue-300 bg-blue-50 text-blue-800"
              : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50"}`}>
            {entry.label}
          </button>
        ))}
      </div>
      <label className="mt-4 block text-sm font-semibold text-gray-700" htmlFor="seec-entity-search">
        Find a {view.label.toLowerCase()}
      </label>
      <input id="seec-entity-search" type="search" value={search} onChange={(event) => setSearch(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Escape") setSearch("");
          if (event.key === "Enter" && matches.length === 1) { event.preventDefault(); select(matches[0]); }
        }}
        placeholder={viewId === "town" ? "Search towns, e.g. Glastonbury" : `Search ${view.label.toLowerCase()} names`}
        className="mt-2 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900" />
      {search.trim() ? (
        <div className="mt-2">
          <p role="status" className="mb-2 text-xs text-gray-500">
            {matches.length === 0 ? "No matches. Try a different name." : `${matches.length.toLocaleString()} matches${matches.length > 10 ? "; showing the first 10. Keep typing to narrow the list." : ". Choose a result to open its filings."}`}
          </p>
          <ul aria-label="Matching filing targets" className="max-h-72 overflow-y-auto rounded-md border border-gray-200 divide-y divide-gray-200">
            {matches.slice(0, 10).map((name) => (
              <li key={name}><button type="button" onClick={() => select(name)}
                className="block w-full bg-white px-3 py-3 text-left text-sm text-gray-800 hover:bg-blue-50">{name}</button></li>
            ))}
          </ul>
        </div>
      ) : activeView ? (
        <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-sm">
          <p className="text-gray-600">Viewing: <span className="font-semibold text-gray-900">{context[activeView.selected]}</span></p>
          <button type="button" onClick={clearSelection} className="bg-transparent text-blue-700 underline hover:text-blue-900">Clear selection</button>
        </div>
      ) : (
        <p className="mt-3 text-sm text-gray-500">Search above to open filings.{viewId === "town" && <> Or <button type="button" onClick={() => select("Glastonbury")} className="bg-transparent text-blue-700 underline hover:text-blue-900">view Glastonbury</button>.</>}</p>
      )}
    </section>
  );
}
